import { useCallback } from "react"
import { useSetRecoilState } from "recoil"
import { auctionParamsSelector } from "../../recoil/atoms/auction"
import {
  isFirstTimeChatResponseAtom,
  newReplyAtom
} from "../../recoil/atoms/chats"
import layoutStates from "../../recoil/atoms/layout"
import { showBadgeOverlayAtom } from "../../recoil/atoms/overlay"
import {
  currentTimeMNPSelector,
  autoPlayMNPAtom,
  playerPropsAtom,
  defaultState,
  currentTimeAtom,
  currentTimeMNPAtom
} from "../../recoil/atoms/player"
import { settingsTrackStatusSelector } from "../../recoil/atoms/settings"
import { usePictureInPicture } from "../usePictureInPicture"
import { viewDependingLayoutAtom } from "../../recoil/atoms/view"
import { productOverlaySelector } from "../../recoil/atoms/product-overlay"
import { pipParamsSelector } from "../../recoil/atoms/pictureInPicture"
import { shoppableVideosListSelector } from "../../recoil/atoms/shoppableVideos"
import {
  localMuteAtom,
  wpLocalMuteControlPropsAtom
} from "../../recoil/atoms/local-mute"
import { shoppableMNPVideoAtom } from "../../recoil/atoms/shoppableVideos"
import { UplinkStatusAtom } from "../../recoil/atoms/remoteVideoStreamType"
import { rtmpStreamsSelector } from "../../recoil/atoms/rtmpStreams"
import { ActionType } from "../../constants/enums"
import { clearStreamValueSelector } from "../../recoil/atoms/streams"
import { screenShareUserSelector } from "../../recoil/atoms/screenShareUser"
import { resetAllRecordingRecoilStateSelector } from "../../recoil/atoms/recording"
import { pollsAtom } from "../../recoil/atoms/polls"
import { ConsoleLogger } from "../../util/logger"
import { useToast } from "@chakra-ui/toast"
import { resetOverlayTimerSelector } from "../../recoil/atoms/timer-overlay"
import { isDurationAlertDisplayedAtom } from "../../recoil/atoms/sessionModalData"
import { clearRoomDurationSetTimeout } from "../useRoomDurationTimeout"
import { useCloseProductPageOverlay } from "../useProductPageOverlay"
import { whiteboardShareUserAtom } from "../../recoil/atoms/whiteboard"

const logger = new ConsoleLogger()

type resetRecoilStateArgument =  { reconnecting?: boolean }

const useCustomResetRecoilState = () => {
  const { exitPictureInPicture } = usePictureInPicture()
  const setViewDependingLayout = useSetRecoilState(viewDependingLayoutAtom)
  const setCurrentTimeMNPProps = useSetRecoilState(currentTimeMNPSelector)
  const setAuctionParams = useSetRecoilState(auctionParamsSelector)
  const setPlayerProps = useSetRecoilState(playerPropsAtom)
  const setCurrentTime = useSetRecoilState(currentTimeAtom)
  const setCurrentTimeMNP = useSetRecoilState(currentTimeMNPAtom)
  const setPlayMNP = useSetRecoilState(autoPlayMNPAtom)
  const setFirstTimeChatResponse = useSetRecoilState(
    isFirstTimeChatResponseAtom
  )
  const setUplinkStatus = useSetRecoilState(UplinkStatusAtom)

  const setShoppableVideos = useSetRecoilState(shoppableVideosListSelector)
  const setPiPParams = useSetRecoilState(pipParamsSelector)
  const setProductSelector = useSetRecoilState(productOverlaySelector)
  const setLocalMute = useSetRecoilState(localMuteAtom)
  const setWpLocalMuteControlProps = useSetRecoilState(
    wpLocalMuteControlPropsAtom
  )
  const setTargetTabIndex = useSetRecoilState<number>(
    layoutStates.historyTabEnabled
  )

  const setShowOnVideoOverlayContent = useSetRecoilState(
    layoutStates.showOnVideoOverlayContent
  )

  const setFirstRenderMobile = useSetRecoilState(layoutStates.firstRenderMobile)
  const setNewReply = useSetRecoilState(newReplyAtom)
  const setSettingsTrackStatus = useSetRecoilState(settingsTrackStatusSelector)
  const setEnabledOverlay = useSetRecoilState(showBadgeOverlayAtom)
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  const setRtmpStreams = useSetRecoilState(rtmpStreamsSelector)
  const clearAllStreamValue = useSetRecoilState(clearStreamValueSelector)
  const resetAllRecordingRecoilState = useSetRecoilState(resetAllRecordingRecoilStateSelector)
  const setScreenShareUser = useSetRecoilState(screenShareUserSelector)
  const setWhiteboardShareUser = useSetRecoilState(whiteboardShareUserAtom)
  const resetOverlayTimerAtoms = useSetRecoilState(resetOverlayTimerSelector)
  const setIsDurationAlertDisplayed = useSetRecoilState(isDurationAlertDisplayedAtom)
  const setPolls = useSetRecoilState(pollsAtom)
  const toast = useToast()
  const closeProductPageOverlay = useCloseProductPageOverlay()

  
  const resetRecoilState = useCallback(({ reconnecting = false }: resetRecoilStateArgument = {}) => {
    logger.log("Rest all recoil state to default")

    if(reconnecting) {
      setFirstTimeChatResponse(true)
      return 
    }

    setTargetTabIndex(0)
    setNewReply(null)
    setViewDependingLayout({
      isDesktop: false,
      isMobile: false,
      isLandscape: false,
      isTablet: false,
      isTransparent: false
    })

    setShowOnVideoOverlayContent(false)
    
    setSettingsTrackStatus(false)
    setPiPParams({ isActivePiP: false, isRedirected: false })
    setProductSelector({ action: "reset" })
    setPlayerProps(defaultState)
    setFirstRenderMobile(false)
    setCurrentTimeMNPProps({ type: "RESET" })
    setCurrentTime(0)
    setCurrentTimeMNP({ currentTime: 0, lastStopCurrentTime: 0 })
    setShoppableVideos({ name: "RESET" })
    setAuctionParams({ isShowOverlay: true })
    setEnabledOverlay(true)
    setPlayMNP(true)
    setFirstTimeChatResponse(true)
    setWpLocalMuteControlProps({ value: false, enabled: false })
    setLocalMute(undefined)
    setShoppableMNPVideo({
      srcShoppableMNP: "",
      enabledShoppableMNP: false,
      idShoppableMNP: "",
      typeShoppableMNP: ""
    })
    setUplinkStatus(-1)
    setRtmpStreams({ action: ActionType.RESET })
    clearAllStreamValue()

    setScreenShareUser(null)
    setWhiteboardShareUser(null)
    
    resetAllRecordingRecoilState()
    resetOverlayTimerAtoms()
    setIsDurationAlertDisplayed(false)
    setPolls([])

    toast.closeAll()
    clearRoomDurationSetTimeout()

    // This function will return whole app from small floating video to origin full size
    closeProductPageOverlay(null)

    // Reset cam and mic status
    // const user = window.MeetOnline.client()?.user
    // if (user && !reconnecting) {
    //   user.camEnabled = true
    //   user.micEnabled = true
    // }
    exitPictureInPicture()
    window.MeetOnline?.client().rtc?.toggleAllMuteUsers(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return resetRecoilState
}

export default useCustomResetRecoilState
